import React, { FC, useCallback, useEffect, useRef } from 'react';
import { loadFontsForDesign } from 'src/components/widgets/preview3D/loaders/designFontLoader';
import { Load3DSceneViewer } from 'src/components/widgets/preview3D/loaders/load3DScene';
import { LoadPreview3D } from 'src/components/widgets/preview3D/loaders/loadPreview3D';
import usePreview3DStore, { Preview3DContext } from 'src/components/widgets/preview3D/store';
import { toGlobalID } from 'src/shared/helpers';
import styled from 'styled-components';
import config from '../../../../../shared/config';
import { useAIStudioProduct } from '../context/productContext';

const HiddenContainer = styled.div`
	position: absolute;
	width: 1024px;
	height: 1024px;
	visibility: hidden;
	pointer-events: none;
	left: -9999px;
`;

/**
 * Selects a variant/color in the 3D scene (same logic as canvasArea's VariantSelector).
 */
const VariantSelector: FC<{ variantId: number }> = ({ variantId }) => {
	const sceneViewer = usePreview3DStore((x) => x.sceneViewer);
	const sceneManager = usePreview3DStore((x) => x.sceneManager);
	const zkProduct = usePreview3DStore((x) => x.zkProduct);
	const zkDesign = usePreview3DStore((x) => x.zkDesign);
	const zkDesignRef = useRef(zkDesign);
	zkDesignRef.current = zkDesign;
	const appliedRef = useRef(false);

	useEffect(() => {
		if (!sceneViewer || !sceneManager || !zkProduct || appliedRef.current) return;

		const selectVariant = async () => {
			try {
				appliedRef.current = true;
				const color = zkProduct.get('colors').find((x: any) => x.get('colorID') === variantId);
				if (!color) return;

				const attributeSelection = sceneManager.getSelectedAttributeOption(color);
				if (attributeSelection?.attributeGuid && attributeSelection?.optionGuid) {
					const zkAttribute = zkProduct.get('attributes').find((x: any) => x.get('attributeGuid') === attributeSelection.attributeGuid);
					if (zkAttribute) {
						const zkOption = zkAttribute.get('options').find((x: any) => x.get('optionGuid') === attributeSelection.optionGuid);
						if (zkOption) {
							const actions = zkOption.get('actions').sortBy((item: any) => item.get('execOrder'));
							await sceneViewer.executeActionsAsync(null, null, actions);
							sceneViewer.adjustCamera(false);
							const design = zkDesignRef.current;
							if (design) {
								sceneViewer.resetDesign();
								await sceneViewer.renderDesignAsync(design);
							}
						}
					}
				}
			} catch (err) {
				console.error('Error selecting variant:', err);
			}
		};
		selectVariant();
	}, [sceneViewer, sceneManager, zkProduct, variantId]);

	return null;
};

/**
 * Loads a design by docId and applies it to the 3D scene (same logic as canvasArea's DesignLoader).
 */
const DesignLoader: FC<{ docId: string }> = ({ docId }) => {
	const sceneViewer = usePreview3DStore((x) => x.sceneViewer);
	const zkProduct = usePreview3DStore((x) => x.zkProduct);
	const setZkDesign = usePreview3DStore((x) => x.setZkDesign);
	const loadedRef = useRef(false);

	useEffect(() => {
		if (!sceneViewer || !zkProduct || !docId || loadedRef.current) return;

		const loadDesign = async () => {
			try {
				loadedRef.current = true;
				const $ = (window as any).jQuery;
				if ($) {
					$.ajaxSetup({ cache: true, headers: { Authorization: 'Bearer ' + config.token } });
					(window as any).$ = $;
					(window as any).jQuery = $;
				}
				const design = new MPlaza.Design({ docID: docId });
				design.setModel(zkProduct);
				await new Promise<void>((resolve, reject) => {
					design.fetch({
						success: () => resolve(),
						error: (response: unknown) => {
							console.error('Failed to fetch design:', response);
							reject(new Error('Failed to fetch design'));
						},
						data: { template: false, frombackoffice: true, fromOrderDesignEditor: false }
					});
				});
				await loadFontsForDesign(design);
				setZkDesign(design);
				sceneViewer.resetDesign();
				await sceneViewer.renderDesignAsync(design);
			} catch (err) {
				console.error('Error loading design:', err);
			}
		};
		loadDesign();
	}, [sceneViewer, zkProduct, docId, setZkDesign]);

	return null;
};

/** Inner component that captures once the scene viewer is ready */
const AutoCaptureViewer: FC<{ onCaptured: (base64: string) => void }> = ({ onCaptured }) => {
	const sceneViewer = usePreview3DStore((x) => x.sceneViewer);
	const capturedRef = useRef(false);
	const containerRef = useRef<HTMLDivElement>(null);
	const { docId, variantId } = useAIStudioProduct();

	useEffect(() => {
		if (!containerRef.current || !sceneViewer) return;
		sceneViewer.appendTo(containerRef.current);
		sceneViewer.updateView(true, true);
		sceneViewer.isSelectionMode = false;
		return () => {
			sceneViewer?.detach();
		};
	}, [sceneViewer]);

	const doCapture = useCallback(async () => {
		if (!sceneViewer || capturedRef.current) return;
		capturedRef.current = true;

		try {
			const screenshot = await sceneViewer.getSceneContentScreenshotAsync(1024, 1024, 0, undefined, false, undefined, '#FFFFFF');
			if (screenshot) onCaptured(screenshot);
		} catch (err) {
			console.error('Auto-capture 3D failed:', err);
		}
	}, [sceneViewer, onCaptured]);

	// Capture once the scene is ready (with a delay for design/variant to apply)
	useEffect(() => {
		if (!sceneViewer) return;
		// If there's a design or variant to load, wait longer for them to complete
		const delay = docId || variantId ? 2000 : 500;
		const timer = setTimeout(() => {
			doCapture();
		}, delay);
		return () => clearTimeout(timer);
	}, [sceneViewer, doCapture, docId, variantId]);

	return (
		<>
			<div ref={containerRef} style={{ width: '100%', height: '100%', position: 'relative' }} />
			{variantId && <VariantSelector variantId={variantId} />}
			{docId && <DesignLoader docId={docId} />}
		</>
	);
};

/** Wrapper that loads the 3D scene with composition/variant from product context */
const Load3DSceneWithComposition: FC<{ children: React.ReactNode; model3D?: string | Blob | null; model3DRootUrl?: string | null }> = ({
	children,
	model3D,
	model3DRootUrl
}) => {
	const { compositionDocId, variantId } = useAIStudioProduct();
	return (
		<Load3DSceneViewer
			fallback={null}
			onProgress={() => {}}
			compositionDocId={compositionDocId}
			variantId={variantId}
			sceneFile={model3D}
			sceneRootUrl={model3DRootUrl}
		>
			{children}
		</Load3DSceneViewer>
	);
};

interface AutoCapture3DProps {
	productId: number;
	model3D?: string | Blob | null;
	model3DRootUrl?: string | null;
	onCaptured: (base64: string) => void;
}

/**
 * Renders a hidden 3D viewer, captures a screenshot once loaded,
 * and calls onCaptured with the base64 data URL.
 * Includes VariantSelector and DesignLoader to render the correct product state.
 */
export const AutoCapture3D: FC<AutoCapture3DProps> = ({ productId, model3D, model3DRootUrl, onCaptured }) => {
	return (
		<HiddenContainer>
			<Preview3DContext>
				<LoadPreview3D productId={toGlobalID(productId, 'Model')} fallback={null} onSceneLoaded={() => {}}>
					<Load3DSceneWithComposition model3D={model3D} model3DRootUrl={model3DRootUrl}>
						<AutoCaptureViewer onCaptured={onCaptured} />
					</Load3DSceneWithComposition>
				</LoadPreview3D>
			</Preview3DContext>
		</HiddenContainer>
	);
};
